import { createFileRoute } from '@tanstack/react-router';
import { CrownIcon } from 'lucide-react';
import { useEffect } from 'react';
import z from 'zod';

import { useGetOrganizationUserQuery } from '@/api/api-hooks';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { EntityNotFound404 } from '@/components/EntityNotFound404';
import { UserAvatar } from '@/components/Settings/UserAvatar';
import { SheetTabs } from '@/components/SheetTabs/SheetTabs';
import { SheetWithHeader } from '@/components/SheetWithHeader';
import { assignmentTypeFilterValues } from '@/components/Sources/assignmentTypeItems';
import { sourceRoleFilterValues } from '@/components/Sources/sourceRoleItems';
import { teamRoleFilterValues } from '@/components/Teams/teamRoleItems';
import { UserOverview } from '@/components/User/Management/UserOverview';
import { UserSources } from '@/components/User/Management/UserSources';
import { UserTeams } from '@/components/User/Management/UserTeams';
import { getUserRoleName } from '@/components/User/userRoleItems';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';
import { optionalSortingSearchParam } from '@/utils/tableSorting';

const tabTypes = ['overview', 'teams', 'sources'] as const;
type TabType = (typeof tabTypes)[number];

const userDetailsParamsSchema = z.object({
  tab: z.enum(tabTypes),
  userId: z.string(),
  organizationName: z.string(),
});

const userDetailsSearchSchema = z.object({
  // UserTeams and UserSources query parameters
  pageSize: z.number().default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
  pageIndex: z.number().default(0).catch(0),
  search: searchParam().optional(),
  // Shared between tabs; cleared on tab switch since each tab has different sortable columns.
  sorting: optionalSortingSearchParam(),
  // UserTeams
  teamRole: z.enum(teamRoleFilterValues).optional(),
  // UserSources
  sourceRole: z.enum(sourceRoleFilterValues).optional(),
  assignment: z.enum(assignmentTypeFilterValues).optional(),
});

export const Route = createFileRoute('/$organizationName/settings/_access/users/$userId/$tab')({
  component: UserSheetPage,
  params: { parse: userDetailsParamsSchema.parse },
  validateSearch: userDetailsSearchSchema,
});

function UserSheetPage() {
  const { organizationDisplayName, routeOrganizationName } = useUser();
  const { userId, tab } = Route.useParams();
  const navigate = Route.useNavigate();

  const {
    data: user,
    isLoading: isLoadingUser,
    isError,
  } = useGetOrganizationUserQuery({ userId: userId }, { refetchOnWindowFocus: false, retry: false });

  const userRole = user?.role;
  const userDisplayName = user?.name || user?.email || '';

  useEffect(() => {
    let section = '';
    switch (tab) {
      case 'overview':
        section = 'Overview';
        break;
      case 'teams':
        section = 'Teams';
        break;
      case 'sources':
        section = 'Sources';
        break;
    }
    document.title = `${organizationDisplayName || 'Driver'} - ${userDisplayName || 'User'} - ${section}`;
  }, [organizationDisplayName, userDisplayName, tab]);

  const isNotFound = isError || (!isLoadingUser && !user);

  return (
    <SheetWithHeader
      isOpen
      renderHeaderSkeleton={isLoadingUser}
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            to: '/$organizationName/settings/users',
            params: { organizationName: routeOrganizationName },
            search: (prev) => ({
              ...prev,
              uPageIndex: undefined,
              uPageSize: undefined,
              uSearch: undefined,
              uRole: undefined,
            }),
          });
        }
      }}
      title={userDisplayName}
      description={
        <span className="flex items-center gap-1">
          <span>{user ? getUserRoleName(userRole ?? 'org_member') : ''}</span>
          {userRole === 'org_super_admin' && <CrownIcon className="h-4 w-4" />}
        </span>
      }
      icon={user ? <UserAvatar user={user} size={40} /> : null}
      widthClassName="w-[90vw] !max-w-[1000px]"
    >
      {isNotFound ? (
        <EntityNotFound404 message="User not found" />
      ) : (
        <SheetTabs
          value={tab}
          onValueChange={(value) =>
            navigate({
              to: '/$organizationName/settings/users/$userId/$tab',
              params: {
                organizationName: routeOrganizationName,
                userId: userId,
                tab: value as TabType,
              },
              // 🐉🐉🐉
              // We need to keep the search params otherwise the useEffect for the debounced search value will be triggered
              // unintentionally and redirect us back to the previous page.
              search: (prev) => ({
                ...prev,
                pageIndex: undefined,
                pageSize: undefined,
                teamRole: undefined,
                sourceRole: undefined,
                search: undefined,
                sorting: undefined,
              }),
            })
          }
          tabs={[
            {
              value: 'overview',
              label: 'Overview',
              className: 'space-y-6',
              content: <UserOverview route={Route} user={user} />,
            },
            {
              value: 'teams',
              label: 'Teams',
              content: <UserTeams route={Route} userId={userId} userName={user?.name} />,
            },
            {
              value: 'sources',
              label: 'Sources',
              content: <UserSources route={Route} userId={userId} userName={user?.name} />,
            },
          ]}
        />
      )}
    </SheetWithHeader>
  );
}
